<script setup>
import { ref, watch, onMounted, nextTick } from 'vue';
import Textarea from '../../../../../components-next/textarea/TextArea.vue';

// Props
const props = defineProps({
  value: {
    type: String,
    default: ''
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

// Emits
const emit = defineEmits(['update']);

// State
const localValue = ref(props.value);
const charCount = ref(props.value.length);
const maxChars = 1500;

// Methods
const handleInput = (value) => {
  localValue.value = value;
  charCount.value = value.length;
  emit('update', value);
};

// Watchers
watch(() => props.value, (newValue) => {
  localValue.value = newValue;
  charCount.value = newValue.length;
});

// Hide character count from Textarea component
const hideTextareaCharCount = () => {
  nextTick(() => {
    const elements = document.querySelectorAll('.flex.items-center.justify-end.h-4.mt-1.bottom-3.ltr\\:right-3.rtl\\:left-3');
    elements.forEach(el => {
      el.style.display = 'none';
    });
  });
};

// Lifecycle
onMounted(() => {
  hideTextareaCharCount();
  // Also watch for DOM changes in case the element is added later
  watch(() => localValue.value, () => {
    hideTextareaCharCount();
  });
});
</script>

<template>
  <div class="custom-prompt-input">
    <div class="relative group">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <div class="w-8 h-8 bg-n-slate-1 rounded-lg flex items-center justify-center">
            <i class="i-lucide-edit-3 text-n-slate-11 text-sm"></i>
          </div>
          <label class="text-sm font-semibold text-n-slate-12">
            Prompt Customizado
          </label>
        </div>
        <div v-if="!disabled && localValue.length > 0" 
             class="text-xs font-medium px-2 py-1 rounded-full bg-n-slate-1 text-n-slate-11">
          <i class="i-lucide-pen-tool mr-1"></i>
          Editando
        </div>
      </div>
      
      <div class="relative">
        <Textarea
          v-model="localValue"
          :disabled="disabled"
          :maxLength="maxChars"
          :autoHeight="true"
          :resize="true"
          :minHeight="'4rem'"
          :maxHeight="'7rem'"
          :showCharacterCount="true"
          :customTextAreaClass="'scrollbar-hide'"
          placeholder="Descreva como você deseja ajustar o comportamento do agente. Por exemplo: 'Torne o agente mais formal e detalhista nas respostas, sempre fornecendo exemplos práticos...'"
          @update:modelValue="handleInput" />
        
        <!-- Character Counter -->
        <div class="absolute bottom-3 right-3 px-2 py-1 pr-0 mr-3 rounded-md text-xs font-medium transition-colors">
          {{ charCount }} / {{ maxChars }}
        </div>
      </div>
    </div>
    
    <!-- Status Messages -->
    <div class="mt-3 flex items-start gap-2">
      <i v-if="disabled" class="i-lucide-info text-n-slate-11 text-sm mt-0.5"></i>
      <i v-else class="i-lucide-lightbulb text-n-brand text-sm mt-0.5"></i>
      
      <div class="flex-1">
        <p v-if="disabled" class="text-sm text-n-slate-11">
          Para usar um prompt customizado, desselecione a sugestão acima primeiro.
        </p>
        <div v-else class="space-y-2">
          <p class="text-sm text-n-slate-11">
            Dicas para um bom prompt:
          </p>
          <ul class="text-xs text-n-slate-10 space-y-1 ml-4">
            <li class="flex items-start gap-2">
              <span class="text-n-brand mt-0.5">•</span>
              <span>Utilize uma linguagem simples e direta, sem termos técnicos ou metáforas</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-n-brand mt-0.5">•</span>
              <span>Descreva comportamentos e respostas esperadas</span>
            </li>
            <li class="flex items-start gap-2">
              <span class="text-n-brand mt-0.5">•</span>
              <span>>Seja claro e específico sobre o que você quer</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
.scrollbar-hide {
  scrollbar-width: none !important; /* Firefox */
  -ms-overflow-style: none !important; /* Internet Explorer 10+ */
}

.scrollbar-hide::-webkit-scrollbar {
  display: none !important; /* WebKit */
}
</style>
